// LocationsSection.tsx · v12.10.28 (#2206 "Additional locations" - the main office is not listed (it is Identity), no External IDs (Integrations › Translations, #2209); #2207 pills inside the section padding, note removed) · 2026-11-02 · VoiceSpectrum AI
// LocationsSection.tsx · v12.10.26 (#2141 T193 Settings › Organization › Locations - list + drawer, Hours & locale pills) · 2026-10-30 · VoiceSpectrum AI
// v12.10.26 (#2141): built to the APPROVED prototype UI-PROTO-T193-LOCATIONS v1.0 (Q1-Q5 settled 10/30). Organization › Locations
// is the ONE list of offices - Hours & locale, Vocabulary › Lists (read-only Locations list), Knowledge Base › Quick Answers and
// the "Which location" workflow step all read it. Each location saves from its own drawer (no section Save). The main location
// is the Identity address / phone (one value), has no Delete and its hours decide open / after-hours for every call.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import './locations.css';

export interface LocationHour {
  dayOfWeek: number;
  isOpen: boolean;
  openTime?: string | null;
  closeTime?: string | null;
  breakEnabled: boolean;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
}

export interface PracticeLocation {
  id: number;
  entryId: string;
  name: string;
  description: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  isMain: boolean;
  hasAnswer: boolean;
  externalIds: Record<string, string>;
  hours: LocationHour[];
}

export interface LocationsPayload {
  locations: PracticeLocation[];
  systems: string[];
  word: string;
}

export const LOCATIONS_KEY = ['t2-org-locations'] as const;
const KB_KEY = ['t2-knowledge-base'];   // KnowledgeBaseSections QKEY

/** v12.10.26 (#2141): the practice's locations, main first then A-Z (the server's order). */
export function useLocations(enabled = true) {
  return useQuery<LocationsPayload>({
    queryKey: LOCATIONS_KEY,
    queryFn: () => api.get<LocationsPayload>('/t2/settings/organization/locations'),
    enabled,
    staleTime: 30_000,
  });
}

const US_STATES = 'AL AK AZ AR CA CO CT DC DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ');

export function locationAddress(l: Pick<PracticeLocation, 'address1' | 'address2' | 'city' | 'state' | 'zip'>): string {
  const stZip = [l.state, l.zip].filter((x) => x && x.trim()).join(' ');
  return [l.address1, l.address2, l.city, stZip].filter((x) => x && x.trim()).join(', ');
}

export function locationPhone(digits?: string | null): string {
  let d = (digits ?? '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d;
}

function typingPhone(value: string): string {
  let d = value.replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string } | undefined;
    return b?.error || e.message;
  }
  return e instanceof Error ? e.message : 'Could not save.';
}

interface DrawerDraft {
  name: string; phone: string; address1: string; address2: string; city: string; state: string; zip: string; description: string;
  externalIds: Record<string, string>;
}

function draftOf(l?: PracticeLocation | null): DrawerDraft {
  return {
    name: l?.name ?? '', phone: l?.phone ? locationPhone(l.phone) : '', address1: l?.address1 ?? '', address2: l?.address2 ?? '',
    city: l?.city ?? '', state: l?.state || 'NJ', zip: l?.zip ?? '', description: l?.description ?? '', externalIds: { ...(l?.externalIds ?? {}) },
  };
}

/** v12.10.26 (#2141): Settings › Organization › Locations - the card (list + "+ Add location") and its drawer. */
export function LocationsCard({ onOpenIdentity, canEdit = true }: { onOpenIdentity?: () => void; canEdit?: boolean }) {
  const qc = useQueryClient();
  const q = useLocations();
  const locations = q.data?.locations ?? [];
  // v12.10.28 (#2206): the card lists the ADDITIONAL offices only - the main office is Identity (address / phone there).
  const additional = locations.filter((l) => !l.isMain);
  const word = q.data?.word || 'Office';
  const [open, setOpen] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<DrawerDraft>(draftOf());
  const [err, setErr] = useState<{ name?: string; phone?: string; save?: string }>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editing = typeof open === 'number' ? locations.find((l) => l.id === open) ?? null : null;

  function refreshAll() {
    qc.invalidateQueries({ queryKey: LOCATIONS_KEY });
    qc.invalidateQueries({ queryKey: ['t2-organization-settings'] });   // the main location IS the Identity address / phone
    qc.invalidateQueries({ queryKey: ['t2-org-vocabulary'] });          // the read-only Locations list
    qc.invalidateQueries({ queryKey: KB_KEY });                          // the per-location Quick Answers
  }

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => (open === 'new'
      ? api.post<LocationsPayload>('/t2/settings/organization/locations', body)
      : api.put<LocationsPayload>(`/t2/settings/organization/locations/${open}`, body)),
    onSuccess: (res) => {
      qc.setQueryData(LOCATIONS_KEY, res);
      refreshAll();
      toast('success', open === 'new' ? 'Location added - its weekly schedule starts Monday to Friday, 9 to 5' : 'Saved - every screen that reads Locations is updated');
      setOpen(null);
    },
    onError: (e) => setErr({ save: errorText(e) }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete<LocationsPayload>(`/t2/settings/organization/locations/${id}`),
    onSuccess: (res) => { qc.setQueryData(LOCATIONS_KEY, res); refreshAll(); toast('success', 'Location deleted'); setConfirmDelete(false); setOpen(null); },
    onError: (e) => { setConfirmDelete(false); setErr({ save: errorText(e) }); },
  });

  function openDrawer(id: number | 'new') {
    const l = id === 'new' ? null : locations.find((x) => x.id === id) ?? null;
    setDraft(draftOf(l));
    setErr({});
    setOpen(id);
  }

  useEffect(() => {
    if (open === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { if (confirmDelete) setConfirmDelete(false); else setOpen(null); }
      if (e.key === 'Enter' && e.ctrlKey) doSave();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft, confirmDelete]);

  function doSave() {
    const e: { name?: string; phone?: string } = {};
    const name = draft.name.trim();
    if (!name) e.name = 'A location needs a name.';
    else if (locations.some((l) => l.id !== open && l.name.trim().toLowerCase() === name.toLowerCase())) e.name = 'Another location already has this name.';
    let d = draft.phone.replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
    if (d && d.length !== 10) e.phone = 'Only US and Canada numbers for now';
    setErr(e);
    if (e.name || e.phone) return;
    save.mutate({
      name, phone: d, address1: draft.address1.trim(), address2: draft.address2.trim(), city: draft.city.trim(), state: draft.state.trim(),
      zip: draft.zip.trim(), description: draft.description.trim(), externalIds: draft.externalIds,
    });
  }

  const patch = (p: Partial<DrawerDraft>) => setDraft((cur) => ({ ...cur, ...p }));

  return (
    <article id="sec-locations" className="settings-section-card loc-card">
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>Additional locations</h2>
          <span className="loc-count">{additional.length} {additional.length === 1 ? 'location' : 'locations'}</span>
        </div>
        <p>Other offices the practice sees patients at. The main office is the address in Identity. Hours &amp; locale, Vocabulary › Lists, Quick Answers and the Which location workflow step read both. Each location saves from its own drawer.</p>
      </header>
      {q.isLoading && <div className="loc-hint">Loading locations…</div>}
      {q.isError && <div className="loc-err">Locations could not be loaded.</div>}
      <div className="loc-list">
        {additional.length === 0 && !q.isLoading && <div className="loc-hint">No additional locations - calls use the main office in Identity.</div>}
        {additional.map((l) => (
          <div key={l.id} className="loc-row" role="button" tabIndex={0} onClick={() => openDrawer(l.id)} onKeyDown={(e) => { if (e.key === 'Enter') openDrawer(l.id); }}>
            <div className="loc-main">
              <div className="loc-name">{l.name}{l.isMain && <><span className="loc-mainb">MAIN</span><span className="loc-lock">🔒 cannot be deleted · address and phone shared with Identity</span></>}</div>
              <div className="loc-addr">{locationAddress(l) || <i className="loc-hint">No address yet</i>}</div>
              <div className={`loc-phone${l.phone ? '' : ' empty'}`}>{l.phone ? locationPhone(l.phone) : 'No phone yet'}</div>
              {l.description && <div className="loc-hint">{l.description}</div>}
            </div>
            <div className="loc-act"><button type="button" className="settings-secondary" onClick={(e) => { e.stopPropagation(); openDrawer(l.id); }}>{canEdit ? 'Edit' : 'View'}</button></div>
          </div>
        ))}
        {canEdit && <button type="button" className="loc-add" onClick={() => openDrawer('new')}>+ Add location</button>}
      </div>

      {open !== null && (
        <div className="pz-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="pz-drawer loc-drawer" role="dialog" aria-modal="true" aria-label={editing ? editing.name : 'New location'}>
            <div className="dw-accent" />
            <div className="dw-head">
              <div><h4>{editing ? editing.name : 'New location'}{editing?.isMain && <span className="loc-mainb">MAIN</span>}</h4><p>{word} · Organization › Locations</p></div>
              <button type="button" className="modal-x" onClick={() => setOpen(null)} aria-label="Close">×</button>
            </div>
            <div className="dw-body">
              {editing?.isMain && (
                <div className="loc-info">🔒 <b>Main location.</b> Its address and phone are the practice&apos;s Identity address and phone - one value, edited here or in{' '}
                  <button type="button" className="loc-link" onClick={() => { setOpen(null); onOpenIdentity?.(); }}>Organization › Identity</button>. It cannot be deleted. Its hours decide open / after-hours for every call.</div>
              )}
              <div className="loc-sec">LOCATION <span className="loc-hint">platform entity · location</span></div>
              <label className="settings-field"><span>Name</span>
                <input value={draft.name} disabled={!canEdit} onChange={(e) => patch({ name: e.target.value })} autoFocus />
                {err.name && <em className="loc-err">{err.name}</em>}</label>
              <label className="settings-field"><span>Phone</span>
                <input value={draft.phone} disabled={!canEdit} placeholder="+1 (___) ___-____" onChange={(e) => patch({ phone: typingPhone(e.target.value) })} />
                {err.phone && <em className="loc-err">{err.phone}</em>}</label>
              <label className="settings-field"><span>Address 1</span><input value={draft.address1} disabled={!canEdit} onChange={(e) => patch({ address1: e.target.value })} /></label>
              <label className="settings-field"><span>Address 2</span><input value={draft.address2} disabled={!canEdit} onChange={(e) => patch({ address2: e.target.value })} /></label>
              <div className="loc-csz">
                <label className="settings-field"><span>City</span><input value={draft.city} disabled={!canEdit} onChange={(e) => patch({ city: e.target.value })} /></label>
                <label className="settings-field"><span>State</span>
                  <select value={draft.state} disabled={!canEdit} onChange={(e) => patch({ state: e.target.value })}>
                    {!US_STATES.includes(draft.state) && draft.state && <option value={draft.state}>{draft.state}</option>}
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select></label>
                <label className="settings-field"><span>ZIP</span><input value={draft.zip} disabled={!canEdit} inputMode="numeric" onChange={(e) => patch({ zip: e.target.value })} /></label>
              </div>
              <label className="settings-field"><span>Description</span><input value={draft.description} disabled={!canEdit} onChange={(e) => patch({ description: e.target.value })} /></label>
              <div className="loc-hint">{editing ? <>VoiceSpectrum ID <code>{editing.entryId}</code> · platform-assigned, read-only</> : 'VoiceSpectrum ID is assigned on save.'}</div>

              <div className="loc-hint">The ID each connected system uses for this location is set in Integrations › the system › Translations.</div>
              {err.save && <div className="loc-err">{err.save}</div>}
            </div>
            <div className="dw-foot">
              {canEdit && editing && !editing.isMain && <button type="button" className="mc pz-danger" style={{ marginRight: 'auto' }} onClick={() => setConfirmDelete(true)}>Delete location</button>}
              <button type="button" className="mc" onClick={() => setOpen(null)}>Cancel <kbd>Esc</kbd></button>
              {canEdit && <button type="button" className="ma" disabled={save.isPending} onClick={doSave}>{save.isPending ? 'Saving…' : 'Save changes'} <kbd>Ctrl+↵</kbd></button>}
            </div>
          </div>
          {confirmDelete && editing && (
            <div className="loc-confirm" role="alertdialog" aria-modal="true" aria-label={`Delete ${editing.name}?`}>
              <div className="loc-confirm-box">
                <h4>Delete {editing.name}?</h4>
                <p>Removes it from Hours &amp; locale, the Vocabulary list, Quick Answers and the <b>Which location</b> step. Past messages keep the name &quot;{editing.name}&quot;.</p>
                <div className="loc-confirm-acts">
                  <button type="button" className="mc" onClick={() => setConfirmDelete(false)}>Cancel</button>
                  <button type="button" className="ma pz-danger" disabled={remove.isPending} onClick={() => remove.mutate(editing.id)}>{remove.isPending ? 'Deleting…' : 'Delete'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/** v12.10.26 (#2141): Hours &amp; locale - one pill per location (only with 2+), the main one selected by default. */
export function LocationPills({ locations, selectedId, onSelect, dirtyIds }: {
  locations: PracticeLocation[]; selectedId: number | null; onSelect: (id: number) => void; dirtyIds?: number[];
}) {
  const main = locations.find((l) => l.isMain);
  const sel = locations.find((l) => l.id === selectedId) ?? main;
  const ordered = useMemo(() => locations, [locations]);
  if (locations.length < 2 || !main || !sel) return null;
  return (
    <div className="loc-pills-wrap">
      <div className="loc-pills" role="tablist" aria-label="Location">
        {ordered.map((l) => (
          <button key={l.id} type="button" role="tab" aria-selected={l.id === sel.id} className={`loc-pill${l.id === sel.id ? ' on' : ''}`} onClick={() => onSelect(l.id)}>
            <span className="loc-pill-dot" />{l.name}{l.isMain && <small> MAIN</small>}{dirtyIds?.includes(l.id) && <span className="d" aria-label="Unsaved changes" />}
          </button>
        ))}
      </div>
    </div>
  );
}
